import { useCallback, useState } from 'react';
import { Box, Form, Icon, Label } from '@alto-avios/alto-ui';

import { useCookies } from '../../hooks/useCookies';
import { Dropdown, DropdownItem } from '../Dropdown/Dropdown';
import { OpCoListContent, useRequiredContent } from '../services/content';
import { Button } from '../Button';
import { Radio } from '../Radio';
import { pushtoGTM } from '../../utilities/pushGTMEvent';
import { SwitcherModal } from '../SwitcherModal';
import { useUIString } from '../services/content/hooks/useUIString';

import { usePreferencesModalState } from './usePreferencesModalState';
import styles from './PreferencesSelector.module.css';

import { useHeaderProps } from '#src/contexts/headerProps/index.ts';

export const PreferencesSelector = () => {
  const { auth, userDetails } = useHeaderProps();
  const { data: opcos } = useRequiredContent(OpCoListContent);

  const { setOpco, setMarket, setLocale, opco, locale } = useCookies();

  const [{ opco: selectedOpCoId, market: selectedMarketId, locale: selectedLocaleId }, setState] = usePreferencesModalState();
  const [isOpen, setIsOpen] = useState(false);

  // current form objects
  const selectedOpCo = opcos.find((opco) => opco.id === selectedOpCoId)!;
  const selectedMarket = selectedOpCo.markets.find((market) => market.id === selectedMarketId)!;

  // options for each field
  const opCoOptions: Array<DropdownItem> = opcos.map((opco) => ({ id: opco.id, image: opco.image, label: opco.label }));
  const marketOptions = selectedOpCo.markets;
  const localeOptions = selectedOpCo.locales;

  const preferencesModalOpCoFieldLabel = useUIString('preferencesModalOpCoFieldLabel');
  const preferencesModalMarketFieldLabel = useUIString('preferencesModalMarketFieldLabel');
  const preferencesModalLanguageFieldLabel = useUIString('preferencesModalLanguageFieldLabel');
  const preferencesModalExternalMarketNotice = useUIString('preferencesModalExternalMarket').replace('{{Market.TargetName}}', selectedMarketId);
  const preferencesModalCta = useUIString('preferencesModalCta');

  const handleChange = useCallback(
    (id: string) => {
      setState({ opco: id });
    },
    [setState]
  );

  const handleSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();

    pushtoGTM(
      'ctaEvent',
      'preferences',
      'select',
      `[1] ${selectedOpCoId.toLowerCase()}, [2] ${selectedMarketId.toLowerCase()}, [3] ${selectedLocaleId.toLowerCase()}`
    );

    if (selectedMarket.thirdPartyUrl) {
      window.open(selectedMarket.thirdPartyUrl, '_blank', 'noopener, noreferrer');
      return;
    }

    const isSwitchingLocale = selectedLocaleId !== locale;

    setOpco(selectedOpCoId);
    setMarket(selectedMarketId);
    setLocale(selectedLocaleId);

    if (isSwitchingLocale) {
      // replace URL segments for current locale with new locale instead
      window.location.href = window.location.href.replace(new RegExp(`\\/${locale}`, 'gi'), `/${selectedLocaleId}`);
    } else {
      window.location.reload();
    }
  };

  return (
    <div data-testid="preferences-selector" className={styles.preferencesSelectorWrapper}>
      <Form
        onSubmit={(event) => {
          event.preventDefault();
          if (userDetails && selectedOpCoId !== opco) {
            setIsOpen(!isOpen);
          } else {
            handleSubmit(event);
          }
        }}
      >
        <Box flexDirection="column" gap="2xs" borderWidth="none">
          <label htmlFor="opco-dropdown" className={styles.label}>
            {preferencesModalOpCoFieldLabel}
          </label>
          <Dropdown
            options={opCoOptions}
            value={selectedOpCoId}
            onChange={handleChange}
            hidePickedLabel={false}
            isForm={true}
            accessibilityId="opco-dropdown"
          />
        </Box>
        <Box flexDirection="column" gap="2xs" borderWidth="none">
          <span id="market-label" className={styles.label}>
            {preferencesModalMarketFieldLabel}
          </span>
          <Box gap="2xs" role="radiogroup" aria-labelledby="market-label" flexWrap="wrap">
            {marketOptions.map((option) => (
              <Radio
                key={option.id}
                className={styles.radioItem}
                value={option.id}
                isChecked={selectedMarketId === option.id}
                name="market"
                onChange={(id) => setState({ market: id })}
                customIcon={option.image}
              >
                {option.name}
              </Radio>
            ))}
          </Box>
        </Box>
        <Box flexDirection="column" gap="2xs" borderWidth="none">
          <span id="locale-label" className={styles.label}>
            {preferencesModalLanguageFieldLabel}
          </span>
          <Box gap="2xs" role="radiogroup" aria-labelledby="locale-label" flexWrap="wrap">
            {localeOptions.map((option) => (
              <Radio
                key={option.id}
                className={styles.radioItem}
                value={option.id}
                isChecked={selectedLocaleId === option.id}
                name="locale"
                onChange={(id) => setState({ locale: id })}
              >
                {option.label}
              </Radio>
            ))}
          </Box>
        </Box>
        {selectedMarket.thirdPartyUrl ? (
          <Box gap="2xs" alignItems="center">
            <Icon iconName="info-circle" />
            <Label size="2xs">{preferencesModalExternalMarketNotice}</Label>
          </Box>
        ) : null}
        <Button type="submit" iconEndProps={selectedMarket.thirdPartyUrl ? { iconName: 'arrow-up-right-from-square' } : undefined}>
          {preferencesModalCta}
        </Button>
      </Form>

      <SwitcherModal
        targetOpCoId={selectedOpCoId}
        auth={auth}
        isOpen={isOpen}
        onClose={() => setIsOpen(!isOpen)}
        onPressCta={() => {
          auth?.logoutProps.onClick?.(selectedOpCoId);
          handleSubmit();
        }}
      />
    </div>
  );
};
